import { createReducer } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import store from 'store'

import { BACKEND_REGISTRATION_CODES } from 'constants/register'
import { STUDENT_ROLE } from 'constants/global'

import {
  getUser,
  register,
  setSelectedCategories,
  setActiveCategory,
  lastPassedCategory,
  setActiveCategoryIndex,
  activate,
  setStep,
  updateAbout,
  createStudentGoal,
  getRandomTest,
  submitTest,
  createBanners,
  fetchAvailableEvents,
  toggleNewCreatedEvent,
  addNewAvailabilities,
  setCreateBannerStep,
  getBanners,
  setTokenSyncComplete,
  setRefreshedTokens,
  getClassBannerCategories,
  getTutorTestRecords,
  getTestCategories,
  switchRole,
  signIn,
  getPaymentOptions,
  deleteClassBanner,
  getBanner,
  resetBanner,
  logout,
  getMainCategories,
  updateNamePicture,
  activateTwoFactorAuth,
  verifyTwoFactorAuth,
  changePassword,
  sendResetPasswordEmail,
  getStudentGoal,
  editStudentGoal,
  resetPassword,
  deactivateTwoFactorAuth,
  deleteAccount,
  cancelTest,
  resetSignInError,
  editClassBanner,
  setSwitchStep,
  getLoginHistory,
  getLinkedDevices,
  logoutFromAllDevices,
  updateCurrentRegistrationStep,
  switchRoleSimple,
  updateRegistraionAbout,
  initiateTest,
  getVerificationStatus,
  getVerification,
  clearInstructionError,
  toggleFullscreen,
  getTutors,
} from './user.action'

const initialState = {
  accessToken: null,
  refreshToken: null,
  fullscreen: false,
  role: [],
  activeRole: '',
  userData: {
    isLoading: false,
    staticLoader: false,
    id: null,
    email: null,
    calendar_settings: {
      advance_booking: '24',
      repeat_weekly: false,
    },
    profile: {
      first_name: '',
      last_name: '',
      username: '',
      gender: '',
      birth_date: '',
      phone_number_part: '',
      country_code: '',
      profile_picture: '',
    },
    user_tutor_profile: null,
    user_student_profile: null,
    registration_step: null,
  },

  isEmailConfirmed: false,
  registerStep: 'create_account',
  switchStep: 1,
  tokenSyncComplete: false,
  activeQuestionNumber: 1,
  activeCategory: null,
  activeCategoryIndex: 0,
  testCategories: [],
  selectedCategories: [],
  lastPassedCategory: null,
  lastTestCategoryName: null,
  passedTests: [],
  currentTest: null,
  lastSubmittedTest: null,
  isLoading: false,
  error: null,
  mainCategories: [],
  loginHistory: {
    loginHistory: [],
    loginHistoryPage: 0,
    loginHistoryPageSize: 0,
    loginHistoryTotal: 0,
  },
  linkedDevices: {
    linkedDevices: [],
    linkedDevicesPage: 0,
    linkedDevicesPageSize: 0,
    linkedDevicesTotal: 0,
  },
  loading: {},
  errors: {
    createAccount: null,
    activation: null,
  },
  paymentOptions: [],
  createBanners: {
    classBannersPage: 0,
    classBannersTotal: 0,
    classBannersPageSize: 0,
    classBannerCategories: [],
    isLoading: false,
    error: null,
    createBannerError: null,
    banners: [],
    banner: null,
    availableEvents: {
      isLoading: false,
      isSubmitting: false,
      newCreated: [],
      data: [],
    },
    step: 1,
  },
  userSignIn: {
    isLoading: false,
    error: '',
  },
  userProfile: {
    isLoading: true,
    error: '',
  },
  goal: null,
  testID: null,
  initiatedTests: null,
  tutorInstructions: {
    isLoading: false,
    error: null,
  },
  verificationData: {
    data: null,
    status: null,
    error: null,
    errorList: null,
    isLoading: false,
  },
  tutors: {
    tutors: [],
    status: null,
    error: null,
    isLoading: false,
  },
}

export { initialState }
const SET_GOAL = 'user/SET_GOAL'

export const setGoal = (goalArray) => ({
  type: SET_GOAL,
  payload: goalArray,
})

const userReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(getUser.fulfilled, (state, action) => {
      if (action.payload && typeof action.payload === 'object') {
        const current_step = action.payload.registration_step
        const roleFromPayload = action.payload.role
        const roleFromStorage = store.get('user_role')
        const currentRole = roleFromPayload || roleFromStorage || STUDENT_ROLE
        state.userData = action.payload
        state.activeRole = currentRole
        state.registerStep = current_step
        state.isEmailConfirmed = current_step !== BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION
        state.userProfile.isLoading = false
      } else {
        console.error('getUser.fulfilled dispatched without a valid object payload!')
        state.errors.getUser = 'Failed to load user data properly.'
      }
    })

    .addCase(getUser.rejected, (state) => {
      state.userProfile.isLoading = false
    })
    .addCase(getUser.pending, (state) => {
      state.userProfile.isLoading = true
    })

    .addCase(signIn.fulfilled, (state, action) => {
      state.userSignIn.isLoading = false
      state.userSignIn.error = null
      state.isAuthenticated = true

      if (action.payload) {
        console.log('[signIn.fulfilled] Received payload:', JSON.stringify(action.payload, null, 2))

        // Ensure action.payload.user is an object, otherwise default to {}
        const incomingUserData =
          typeof action.payload.user === 'object' && action.payload.user !== null
            ? action.payload.user
            : {}

        // Set user data and tokens
        state.userData = incomingUserData

        // Ensure tokens are properly set in Redux state
        if (action.payload.access) {
          state.accessToken = action.payload.access
          // Directly set the raw token value, not as a string containing a string
          store.set('access_token', action.payload.access)
          console.log(
            `[signIn.fulfilled] Access token set in Redux and localStorage, length: ${action.payload.access.length}`
          )
        }

        if (action.payload.refresh) {
          state.refreshToken = action.payload.refresh
          // Directly set the raw token value, not as a string containing a string
          store.set('refresh_token', action.payload.refresh)
          console.log(
            `[signIn.fulfilled] Refresh token set in Redux and localStorage, length: ${action.payload.refresh.length}`
          )
        }

        // Set role - prioritize payload.role, then user.role, then stored role, then default
        const roleFromPayload = action.payload.role || incomingUserData.role
        const roleFromStorage = store.get('user_role')
        const currentRole = roleFromPayload || roleFromStorage || STUDENT_ROLE

        state.activeRole = currentRole
        state.role = currentRole
        store.set('user_role', currentRole)

        // Handle registration step
        const registrationStep =
          action.payload.registration_step ||
          incomingUserData.profile?.current_registration_step ||
          BACKEND_REGISTRATION_CODES.CREATE_ACCOUNT

        // Only update the step if it's not already set to a later step
        if (
          !state.registerStep ||
          Object.values(BACKEND_REGISTRATION_CODES).indexOf(registrationStep) >=
            Object.values(BACKEND_REGISTRATION_CODES).indexOf(state.registerStep)
        ) {
          state.registerStep = registrationStep
          store.set('register_step', registrationStep)
        }

        console.log('[signIn.fulfilled] User authentication successful:', {
          userId: state.userData?.id,
          role: state.role,
          registrationStep: state.registerStep,
          isEmailConfirmed: incomingUserData.is_email_confirmed,
        })

        // Handle calendar_settings default only if userData is an object and calendar_settings is missing
        if (typeof state.userData === 'object' && state.userData !== null) {
          if (!state.userData.calendar_settings) {
            console.warn(
              '[signIn.fulfilled] User data is present but calendar_settings missing. Using mock settings.'
            )
            state.userData.calendar_settings = {
              advance_booking: '24',
              repeat_weekly: false,
            }
          }

          // Ensure the current registration step is set in the user profile
          if (state.userData.profile) {
            state.userData.registration_step = state.registerStep
          }
        }
      } else {
        console.error('signIn.fulfilled dispatched without a valid payload!', action.payload)
        state.userSignIn.error = 'Login failed: Received unexpected data.'
        state.accessToken = null
        state.refreshToken = null
        state.activeRole = ''
        state.role = []
        state.isAuthenticated = false
      }
    })
    .addCase(signIn.pending, (state) => {
      state.userSignIn.isLoading = true
      state.userSignIn.error = null
    })
    .addCase(signIn.rejected, (state, action) => {
      state.userSignIn.isLoading = false
      state.userSignIn.error = action.payload?.detail || action.error?.message || null
    })

    .addCase(register.fulfilled, (state, action) => {
      state.isLoading = false
      state.errors.createAccount = null

      // Update user data from the payload
      if (action.payload.user) {
        state.userData = action.payload.user
      } else {
        // Fallback to the payload data if user object is not available
        state.userData = {
          ...state.userData,
          ...action.payload,
          profile: {
            ...(state.userData?.profile || {}),
            ...(action.payload.profile || {}),
          },
        }
      }

      // Set role from meta or payload
      const userRole = action.meta?.arg?.user_type || state.userData.role || STUDENT_ROLE
      state.role = userRole
      state.activeRole = userRole

      // Store the role in local storage
      store.set('user_role', userRole)

      // Set the initial registration step
      const initialStep =
        action.payload.registration_step ||
        action.payload.user?.profile?.current_registration_step ||
        BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION

      state.registerStep = initialStep
      store.set('register_step', initialStep)

      // Ensure the user data has the current registration step
      if (state.userData.profile) {
        state.userData.registration_step = initialStep
      }

      console.log('[Reducer] Registration successful, step set to:', initialStep, 'Role:', userRole)
    })
    .addCase(register.pending, (state, action) => {
      state.isLoading = true
      state.errors.createAccount = null
    })
    .addCase(register.rejected, (state, action) => {
      state.isLoading = false
      if (action.payload) {
        state.errors.createAccount = action.payload
      } else {
        state.errors.createAccount = {
          detail: action.error?.message || 'An unknown registration error occurred.',
        }
      }
    })

    .addCase(activate.fulfilled, (state, action) => {
      state.userData.isLoading = false
      state.error = null

      if (action.payload) {
        if (action.payload.access) {
          state.accessToken = action.payload.access
          console.log('[Reducer activate.fulfilled] accessToken set in Redux state')
        }
        if (action.payload.refresh) {
          state.refreshToken = action.payload.refresh
          console.log('[Reducer activate.fulfilled] refreshToken set in Redux state')
        }

        // Set email confirmation and registration step regardless of user data presence
        state.isEmailConfirmed = true

        // CRITICAL: Set registerStep to email_verification to show the confirmation screen
        state.registerStep = BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION
        console.log(
          '[Reducer activate.fulfilled] Setting registerStep to',
          BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION
        )

        // Store the registration step in localStorage to maintain consistent state
        store.set('register_step', BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION)
        localStorage.setItem('register_step', BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION)

        // Get the user role from the payload (set in the action) or from localStorage
        const roleFromPayload = action.payload.role
        const roleFromStorage = store.get('user_role')

        // Log role sources for debugging
        console.log('[Reducer activate.fulfilled] Role sources:', {
          fromPayload: roleFromPayload,
          fromStorage: roleFromStorage,
        })

        // Set the role in the state - prioritize payload, then storage, then default
        const currentRole = roleFromPayload || roleFromStorage || STUDENT_ROLE
        state.activeRole = currentRole
        state.role = currentRole

        // Ensure consistent storage
        store.set('user_role', currentRole)
        localStorage.setItem('user_role', currentRole)

        console.log('[Reducer activate.fulfilled] Final role set to:', currentRole)

        if (action.payload.user) {
          state.userData = action.payload.user

          // If user profile exists, update the current_registration_step
          if (state.userData.profile) {
            state.userData.registration_step = BACKEND_REGISTRATION_CODES.EMAIL_VERIFICATION
          }

          console.log('[Reducer activate.fulfilled] User data PRESENT in payload')
        } else {
          console.log('[Reducer activate.fulfilled] User data MISSING from payload')
        }
      } else {
        console.error('[Reducer activate.fulfilled] Payload was missing.')
      }
    })
    .addCase(activate.rejected, (state, action) => {
      state.userData.isLoading = false
      if (action.payload && action.payload.detail) {
        const errorMessage = action.payload.detail
        // state.errors.activation = errorMessage

        if (
          errorMessage.toLowerCase().includes('user is already active') ||
          errorMessage.toLowerCase().includes('already activated')
        ) {
          state.isEmailConfirmed = true
          state.errors.activation = null
          console.log(
            '[Reducer activate.rejected] User already active (explicit msg), setting isEmailConfirmed to true.'
          )
        } else if (errorMessage.toLowerCase().includes('the token is invalid.')) {
          if (state.accessToken) {
            state.isEmailConfirmed = true
            // state.errors.activation = 'Email has already been verified'
            console.log(
              '[Reducer activate.rejected] Token invalid, but user has existing session. Assuming already active.'
            )
          } else {
            state.isEmailConfirmed = true
            state.errors.activation = 'This account has already been verified. You can now log in'
            console.log(
              '[Reducer activate.rejected] Token invalid, and no existing session. Displaying error.'
            )
          }
        }
      } else {
        state.errors.activation = action.error?.message || 'An unknown activation error occurred.'
      }
    })

    .addCase(updateAbout.fulfilled, (state, action) => {
      state.userData.profile = action.payload.profile
      state.userData.user_tutor_profile = action.payload.user_tutor_profile
      state.userData.user_student_profile = action.payload.user_student_profile
      state.isLoading = false
      state.errors.updateAbout = null
    })
    .addCase(updateAbout.pending, (state) => {
      state.isLoading = true
      state.errors.updateAbout = null
    })
    .addCase(updateAbout.rejected, (state, action) => {
      state.errors.updateAbout = action.error.message
      state.isLoading = false
    })

    .addCase(updateRegistraionAbout.fulfilled, (state, action) => {
      state.userData = action.payload
      state.isLoading = false
      state.errors.updateAbout = null
    })
    .addCase(updateRegistraionAbout.pending, (state) => {
      state.isLoading = true
      state.errors.updateAbout = null
    })
    .addCase(updateRegistraionAbout.rejected, (state, action) => {
      state.errors.updateAbout = action.payload.error ?? action.error.message
      state.isLoading = false
    })

    .addCase(updateNamePicture.fulfilled, (state, action) => {
      state.loading.updateNamePicture = false
      state.errors.updateNamePicture = null

      const role = action.meta.arg.role
      const profile = state.userData?.[`user_${role}_profile`]

      if (profile) {
        if (action.payload.first_name) {
          profile.first_name = action.payload.first_name
        }

        if (action.payload.last_name) {
          profile.last_name = action.payload.last_name
        }

        if (action.payload.username) {
          profile.username = action.payload.username
        }

        profile.profile_picture = action.payload.profile_picture

        store.set('user_data', {
          ...state.userData,
          [`user_${role}_profile`]: profile,
        })
      } else {
        state.userData[`user_${role}_profile`] = action.payload

        store.set('user_data', {
          ...state.userData,
          [`user_${role}_profile`]: action.payload,
        })
      }
    })
    .addCase(updateNamePicture.pending, (state) => {
      state.loading.updateNamePicture = true
      state.errors.updateNamePicture = null
    })
    .addCase(updateNamePicture.rejected, (state, action) => {
      state.loading.updateNamePicture = false
      state.errors.updateNamePicture = action.error?.message || null
    })

    .addCase(createStudentGoal.fulfilled, (state, action) => {
      state.goal = action.payload
      state.loading.createStudentGoal = false
      state.errors.createStudentGoal = false
    })
    .addCase(createStudentGoal.pending, (state) => {
      state.loading.createStudentGoal = true
      state.errors.createStudentGoal = false
    })
    .addCase(createStudentGoal.rejected, (state) => {
      state.loading.createStudentGoal = false
      state.errors.createStudentGoal = true
    })

    .addCase(getRandomTest.fulfilled, (state, action) => {
      state.currentTest = action.payload.data
      state.loading.getRandomTest = false
      state.errors.getRandomTest = null
    })
    .addCase(getRandomTest.pending, (state) => {
      state.loading.getRandomTest = true
      state.errors.getRandomTest = null
    })
    .addCase(getRandomTest.rejected, (state, action) => {
      state.loading.getRandomTest = false
      state.errors.getRandomTest = action.payload ?? action.error?.message
    })

    .addCase(cancelTest.fulfilled, (state) => {
      state.currentTest = null
      state.loading.cancelTest = false
      state.errors.cancelTest = false
    })
    .addCase(cancelTest.pending, (state) => {
      state.loading.cancelTest = true
      state.errors.cancelTest = false
    })
    .addCase(cancelTest.rejected, (state) => {
      state.loading.cancelTest = false
      state.errors.cancelTest = true
    })

    .addCase(submitTest.fulfilled, (state, action) => {
      state.lastSubmittedTest = action.payload
      state.passedTests.push({
        test: action.payload,
        category: state.lastPassedCategory,
        name: action.payload.category_name,
      })

      state.lastTestCategoryName = action.payload.category_name
      state.loading.submitTest = false
      state.errors.submitTest = false
    })
    .addCase(submitTest.pending, (state) => {
      state.loading.submitTest = true
      state.errors.submitTest = false
    })
    .addCase(submitTest.rejected, (state) => {
      state.loading.submitTest = false
      state.errors.submitTest = true
    })

    .addCase(setSelectedCategories, (state, action) => {
      state.selectedCategories = action.payload
    })
    .addCase(setActiveCategory, (state, action) => {
      state.activeCategorySlug = action.payload
    })
    .addCase(lastPassedCategory, (state, action) => {
      state.lastPassedCategory = action.payload
    })
    .addCase(setActiveCategoryIndex, (state, action) => {
      state.activeCategoryIndex = action.payload
    })
    .addCase(clearInstructionError, (state) => {
      state.tutorInstructions.error = null
    })
    .addCase(toggleFullscreen, (state, action) => {
      state.fullscreen = action.payload
    })
    // setStep is now handled by the async thunk
    .addCase(setStep.fulfilled, (state, action) => {
      // This is just a fallback, the step is already set in the thunk
      state.registerStep = action.payload

      // Ensure the user data in Redux is updated with the new step
      if (state.userData?.profile) {
        state.registerStep = action.payload
        state.userData.registration_step = action.payload
      }
    })

    .addCase(getTestCategories.fulfilled, (state, action) => {
      state.testCategories = action.payload
    })

    .addCase(createBanners.fulfilled, (state, action) => {
      state.createBanners.isLoading = false
      state.createBanners.createBannerError = null

      state.createBanners.step += 1
      state.createBanners.banners.push(action.payload)
    })
    .addCase(createBanners.pending, (state) => {
      state.createBanners.isLoading = true
      state.createBanners.createBannerError = null
    })
    .addCase(createBanners.rejected, (state, action) => {
      state.createBanners.isLoading = false
      state.createBanners.createBannerError = action.payload?.message || action.payload
    })

    .addCase(getBanners.fulfilled, (state, action) => {
      if (action.payload) {
        state.createBanners.banners = action.payload.results || []
        state.createBanners.classBannersPage = action.payload.page
        state.createBanners.classBannersTotal = action.payload.total
        state.createBanners.classBannersPageSize = action.payload.page_size
      } else {
        console.error('getBanners.fulfilled dispatched without a payload!')
        state.errors.getBanners = 'Failed to load banners: Invalid data received.'
      }
      state.loading.getBanners = false
    })
    .addCase(getBanners.pending, (state) => {
      state.loading.getBanners = true
      state.errors.getBanners = false
    })
    .addCase(getBanners.rejected, (state) => {
      state.loading.getBanners = false
      state.errors.getBanners = true
    })

    .addCase(getClassBannerCategories.fulfilled, (state, action) => {
      if (action.payload) {
        state.createBanners.classBannerCategories = action.payload
      } else {
        console.error('getClassBannerCategories.fulfilled dispatched without a payload!')
      }
    })
    .addCase(getTutorTestRecords.fulfilled, (state, action) => {
      if (action.payload && Array.isArray(action.payload.results)) {
        state.passedTests = action.payload.results.map((test) => {
          return {
            test: {
              ...test,
              is_passed: test.result === 'Pass',
            },
            category:
              state.selectedCategories.find((category) => category.id === test.category) ||
              state.mainCategories.find((category) => category.id === test.category),
          }
        })
      } else {
        console.error('getTutorTestRecords.fulfilled dispatched without a valid results array!')
        state.passedTests = []
      }
    })
    .addCase(fetchAvailableEvents.fulfilled, (state, action) => {
      state.createBanners.availableEvents.isLoading = false
      if (action.payload && action.payload.results && Array.isArray(action.payload.results.data)) {
        state.createBanners.availableEvents.data = action.payload.results.data
      } else {
        console.error('fetchAvailableEvents.fulfilled dispatched without valid results.data array!')
        state.createBanners.availableEvents.data = []
        state.createBanners.error = 'Failed to load available events: Invalid data.'
      }
    })

    .addCase(addNewAvailabilities.fulfilled, (state) => {
      state.createBanners.availableEvents.isSubmitting = false
      state.createBanners.availableEvents.data = state.createBanners.availableEvents.data.concat(
        state.createBanners.availableEvents.newCreated
      )
      state.createBanners.availableEvents.newCreated = []

      state.createBanners.error = null
    })
    .addCase(addNewAvailabilities.pending, (state) => {
      state.createBanners.availableEvents.isSubmitting = true
      state.createBanners.error = null
    })
    .addCase(addNewAvailabilities.rejected, (state, action) => {
      state.createBanners.availableEvents.isSubmitting = false
      state.createBanners.error = action.error.message
    })

    .addCase(fetchAvailableEvents.pending, (state) => {
      state.createBanners.availableEvents.isLoading = true
      state.createBanners.error = null
    })

    .addCase(getPaymentOptions.fulfilled, (state, action) => {
      if (action.payload) {
        state.paymentOptions = action.payload
      } else {
        console.error('getPaymentOptions.fulfilled dispatched without a payload!')
      }
    })
    .addCase(deleteClassBanner.fulfilled, (state) => {
      state.loading.deleteClassBanner = false
      state.errors.deleteClassBanner = false
    })
    .addCase(deleteClassBanner.pending, (state) => {
      state.loading.deleteClassBanner = true
      state.errors.deleteClassBanner = false
    })
    .addCase(deleteClassBanner.rejected, (state) => {
      state.loading.deleteClassBanner = false
      state.errors.deleteClassBanner = true
    })

    .addCase(editClassBanner.fulfilled, (state) => {
      state.loading.editClassBanner = false
      state.errors.editClassBanner = null
    })
    .addCase(editClassBanner.pending, (state) => {
      state.loading.editClassBanner = true
      state.errors.editClassBanner = null
    })
    .addCase(editClassBanner.rejected, (state, action) => {
      state.loading.editClassBanner = false
      state.errors.editClassBanner = action.error?.message || null
    })

    .addCase(getBanner.fulfilled, (state, action) => {
      if (action.payload) {
        state.createBanners.banner = action.payload
      } else {
        console.error('getBanner.fulfilled dispatched without a payload!')
        state.errors.getBanner = 'Failed to load banner details: Invalid data.'
      }
      state.loading.getBanner = false
    })
    .addCase(getBanner.pending, (state) => {
      state.loading.getBanner = true
      state.errors.getBanner = false
    })
    .addCase(getBanner.rejected, (state) => {
      state.loading.getBanner = false
      state.errors.getBanner = true
    })

    .addCase(getMainCategories.fulfilled, (state, action) => {
      if (action.payload) {
        state.mainCategories = action.payload
      } else {
        console.error('getMainCategories.fulfilled dispatched without a payload!')
        state.errors.getMainCategories = 'Failed to load main categories: Invalid data.'
      }
      state.loading.getMainCategories = false
    })
    .addCase(getMainCategories.pending, (state) => {
      state.loading.getMainCategories = true
      state.errors.getMainCategories = false
    })
    .addCase(getMainCategories.rejected, (state) => {
      state.loading.getMainCategories = false
      state.errors.getMainCategories = true
    })

    .addCase(activateTwoFactorAuth.fulfilled, (state) => {
      state.loading.activateTwoFactorAuth = false
      state.errors.activateTwoFactorAuth = false
    })
    .addCase(activateTwoFactorAuth.pending, (state) => {
      state.loading.activateTwoFactorAuth = true
      state.errors.activateTwoFactorAuth = false
    })
    .addCase(activateTwoFactorAuth.rejected, (state) => {
      state.loading.activateTwoFactorAuth = false
      state.errors.activateTwoFactorAuth = true
    })

    .addCase(deactivateTwoFactorAuth.fulfilled, (state) => {
      state.userData.is_two_fa = false

      state.loading.deactivateTwoFactorAuth = false
      state.errors.deactivateTwoFactorAuth = false
    })
    .addCase(deactivateTwoFactorAuth.pending, (state) => {
      state.loading.deactivateTwoFactorAuth = true
      state.errors.deactivateTwoFactorAuth = false
    })
    .addCase(deactivateTwoFactorAuth.rejected, (state) => {
      state.loading.deactivateTwoFactorAuth = false
      state.errors.deactivateTwoFactorAuth = true
    })

    .addCase(verifyTwoFactorAuth.fulfilled, (state) => {
      state.userData.is_two_fa = true

      state.loading.verifyTwoFactorAuth = false
      state.errors.verifyTwoFactorAuth = null
    })
    .addCase(verifyTwoFactorAuth.pending, (state) => {
      state.loading.verifyTwoFactorAuth = true
      state.errors.verifyTwoFactorAuth = null
    })
    .addCase(verifyTwoFactorAuth.rejected, (state, action) => {
      state.loading.verifyTwoFactorAuth = false
      state.errors.verifyTwoFactorAuth = action.error?.message || null
    })

    .addCase(changePassword.fulfilled, (state) => {
      state.loading.changePassword = false
      state.errors.changePassword = false
    })
    .addCase(changePassword.pending, (state) => {
      state.loading.changePassword = true
      state.errors.changePassword = false
    })
    .addCase(changePassword.rejected, (state) => {
      state.loading.changePassword = false
      state.errors.changePassword = true
    })

    .addCase(sendResetPasswordEmail.fulfilled, (state) => {
      state.loading.sendResetPasswordEmail = false
      state.errors.sendResetPasswordEmail = false
    })
    .addCase(sendResetPasswordEmail.pending, (state) => {
      state.loading.sendResetPasswordEmail = true
      state.errors.sendResetPasswordEmail = false
    })
    .addCase(sendResetPasswordEmail.rejected, (state, action) => {
      state.loading.sendResetPasswordEmail = false
      state.errors.sendResetPasswordEmail = action.payload?.details || 'Something went wrong'
    })

    .addCase(getStudentGoal.fulfilled, (state, action) => {
      state.goal = action.payload

      state.loading.getStudentGoal = false
      state.errors.getStudentGoal = false
    })
    .addCase(getStudentGoal.pending, (state) => {
      state.loading.getStudentGoal = true
      state.errors.getStudentGoal = false
    })
    .addCase(getStudentGoal.rejected, (state) => {
      state.loading.getStudentGoal = false
      state.errors.getStudentGoal = true
    })

    .addCase(editStudentGoal.fulfilled, (state, action) => {
      state.goal = action.payload

      state.loading.editStudentGoal = false
      state.errors.editStudentGoal = false
    })
    .addCase(editStudentGoal.pending, (state) => {
      state.loading.editStudentGoal = true
      state.errors.editStudentGoal = false
    })
    .addCase(editStudentGoal.rejected, (state) => {
      state.loading.editStudentGoal = false
      state.errors.editStudentGoal = true
    })

    .addCase(resetPassword.fulfilled, (state) => {
      state.loading.resetPassword = false
      state.errors.resetPassword = false
    })
    .addCase(resetPassword.pending, (state) => {
      state.loading.resetPassword = true
      state.errors.resetPassword = false
    })
    .addCase(resetPassword.rejected, (state, action) => {
      state.loading.resetPassword = false
      state.errors.resetPassword = action.error.message
    })

    .addCase(getLoginHistory.fulfilled, (state, action) => {
      if (action.payload) {
        state.loginHistory.loginHistory = action.payload.results || []
        state.loginHistory.loginHistoryPage = action.payload.page
        state.loginHistory.loginHistoryTotal = action.payload.total
        state.loginHistory.loginHistoryPageSize = action.payload.page_size
      } else {
        console.error('getLoginHistory.fulfilled dispatched without a payload!')
        state.errors.getLoginHistory = 'Failed to load login history: Invalid data.'
      }
      state.loading.getLoginHistory = false
    })
    .addCase(getLoginHistory.pending, (state) => {
      state.loading.getLoginHistory = true
      state.errors.getLoginHistory = false
    })
    .addCase(getLoginHistory.rejected, (state) => {
      state.loading.getLoginHistory = false
      state.errors.getLoginHistory = true
    })

    .addCase(getLinkedDevices.fulfilled, (state, action) => {
      if (action.payload) {
        state.linkedDevices.linkedDevices = action.payload.results || []
        state.linkedDevices.linkedDevicesPage = action.payload.page
        state.linkedDevices.linkedDevicesTotal = action.payload.total
        state.linkedDevices.linkedDevicesPageSize = action.payload.page_size
      } else {
        console.error('getLinkedDevices.fulfilled dispatched without a payload!')
        state.errors.getLinkedDevices = 'Failed to load linked devices: Invalid data.'
      }
      state.loading.getLinkedDevices = false
    })
    .addCase(getLinkedDevices.pending, (state) => {
      state.loading.getLinkedDevices = true
      state.errors.getLinkedDevices = false
    })
    .addCase(getLinkedDevices.rejected, (state) => {
      state.loading.getLinkedDevices = false
      state.errors.getLinkedDevices = true
    })

    .addCase(logoutFromAllDevices.fulfilled, (state) => {
      state.loading.logoutFromAllDevices = false
      state.errors.logoutFromAllDevices = false
    })
    .addCase(logoutFromAllDevices.pending, (state) => {
      state.loading.logoutFromAllDevices = true
      state.errors.logoutFromAllDevices = false
    })
    .addCase(logoutFromAllDevices.rejected, (state) => {
      state.loading.logoutFromAllDevices = false
      state.errors.logoutFromAllDevices = true
    })

    .addCase(deleteAccount.fulfilled, (state) => {
      store.set('access_token', null)
      store.set('refresh_token', null)
      store.set('user_data', null)

      state.loading.deleteAccount = false
      state.errors.deleteAccount = false
    })
    .addCase(deleteAccount.pending, (state) => {
      state.loading.deleteAccount = true
      state.errors.deleteAccount = false
    })
    .addCase(deleteAccount.rejected, (state) => {
      state.loading.deleteAccount = false
      state.errors.deleteAccount = true
    })

    .addCase(toggleNewCreatedEvent, (state, action) => {
      const key = `${action.payload.day}:${action.payload.slot}`
      const alreadyExistIndex = state.createBanners.availableEvents.newCreated.findIndex(
        (e) => `${e.day}:${e.slot}` === key
      )
      if (alreadyExistIndex > -1) {
        state.createBanners.availableEvents.newCreated.splice(alreadyExistIndex, 1)
      } else {
        state.createBanners.availableEvents.newCreated.push(action.payload)
      }
    })

    .addCase(setCreateBannerStep, (state, action) => {
      state.createBanners.step = action.payload
    })

    .addCase(setSwitchStep, (state, action) => {
      store.set('switch_step', action.payload)
      state.switchStep = action.payload
    })

    // Handle the simple switchRole action
    .addCase(switchRoleSimple, (state, action) => {
      state.activeRole = action.payload
      state.role = action.payload
    })

    // Handle the enhanced switchRole thunk
    .addCase(switchRole.fulfilled, (state, action) => {
      // The role is already updated by switchRoleSimple
      // Here we just handle any additional state updates
      console.log(
        `[Reducer] Role switch to ${action.payload.role} complete, step: ${action.payload.step}`
      )
      if (action.payload.step) {
        state.registerStep = action.payload.step
        store.set('register_step', action.payload.step)
      }
    })

    .addCase(resetBanner, (state) => {
      state.createBanners.banner = null
      state.loading.getBanner = false
      state.errors.getBanner = false
    })

    .addCase(resetSignInError, (state) => {
      state.userSignIn.error = null
    })

    .addCase(logout.fulfilled, (state) => {
      store.set('access_token', null)
      store.set('refresh_token', null)
      store.set('user_data', null)
      store.set('register_step', 'create_account')
      store.set('switch_step', 1)

      return {
        ...initialState,
        switchStep: 1,
        accessToken: null,
        refreshToken: null,
        userData: null,
        role: state.role || STUDENT_ROLE,
      }
    })

    .addCase('TOKEN_SYNC_COMPLETE', (state, action) => {
      if (action.payload) {
        if (action.payload.token) {
          state.accessToken = action.payload.token
          console.log(
            '[Reducer TOKEN_SYNC_COMPLETE] accessToken synchronized from localStorage to Redux state'
          )
        }
        if (action.payload.refreshToken) {
          state.refreshToken = action.payload.refreshToken
          console.log(
            '[Reducer TOKEN_SYNC_COMPLETE] refreshToken synchronized from localStorage to Redux state'
          )
        }
      } else {
        console.warn('[Reducer TOKEN_SYNC_COMPLETE] Dispatched without a payload.')
      }
    })

    .addCase(setTokenSyncComplete, (state, action) => {
      state.tokenSyncComplete = action.payload
      console.log(`[Reducer SET_TOKEN_SYNC_COMPLETE] tokenSyncComplete set to: ${action.payload}`)
    })
    .addCase(setRefreshedTokens, (state, action) => {
      if (action.payload?.access) {
        state.accessToken = action.payload.access
        // Assuming request.js already updated localStorage and store.js for these
        console.log('[Reducer setRefreshedTokens] Access token updated in Redux state.')
      }
      if (action.payload?.refresh) {
        state.refreshToken = action.payload.refresh
        console.log('[Reducer setRefreshedTokens] Refresh token updated in Redux state.')
      }
    })

    .addCase(updateCurrentRegistrationStep.pending, (state) => {
      state.loading.updateCurrentRegistrationStep = true
      state.errors.updateCurrentRegistrationStep = null
    })
    .addCase(updateCurrentRegistrationStep.fulfilled, (state, action) => {
      state.loading.updateCurrentRegistrationStep = false
      state.errors.updateCurrentRegistrationStep = null
      // Handle the response from updateCurrentRegistrationStep
      if (action.payload) {
        const { current_registration_step, user } = action.payload

        // Update the registerStep in Redux state
        if (current_registration_step) {
          state.registerStep = current_registration_step
          store.set('register_step', current_registration_step)
        }

        // Update the user data in Redux with the new step
        if (user) {
          state.userData = user
        } else if (state.userData?.profile) {
          state.userData.registration_step = current_registration_step
        }

        console.log('[Reducer] Updated registration step to:', current_registration_step)
      }
    })
    .addCase(SET_GOAL, (state, action) => {
      return {
        ...state,
      }
    })
    .addCase(updateCurrentRegistrationStep.rejected, (state, action) => {
      state.loading.updateCurrentRegistrationStep = false
      state.errors.updateCurrentRegistrationStep = action.error?.message || null
    })
    .addCase('USER_RESET_STATE', (state) => {
      // Reset critical state to initial values
      state.accessToken = null
      state.refreshToken = null
      state.userData = null
      state.registerStep = 'create_account'
      state.role = ''
      state.activeRole = ''
      state.isEmailConfirmed = false
      state.userSignIn = { isLoading: false, error: null }
      state.errors = {
        ...state.errors,
        createAccount: null,
        activation: null,
        signIn: null,
      }
    })
    .addCase(initiateTest.fulfilled, (state, action) => {
      return {
        ...state,
        testIDs: action.payload.initiatedTestIds,
        initiatedTests: action.payload.initiatedTests,
        selectedCategories: action.payload.initiatedTests,
      }
    })
    .addCase(initiateTest.rejected, (state, action) => {
      return {
        ...state,
        tutorInstructions: {
          error: action.payload,
        },
      }
    })
    .addCase(initiateTest.pending, (state, action) => {
      return {
        ...state,
        tutorInstructions: {
          error: null,
          isLoading: true,
        },
      }
    })

    .addCase(getVerificationStatus.fulfilled, (state, action) => {
      const errorList = Object.entries(action.payload)
        .filter(([key, value]) => typeof value === 'string' && value.length > 0 && key !== 'status')
        .map(([key, value]) => ({ field: key, message: value }))
      return {
        ...state,
        verificationData: {
          status: action.payload.status,
          isLoading: false,
          errorList: errorList,
          data: action.payload.decision_data,
        },
      }
    })
    .addCase(getVerificationStatus.rejected, (state, action) => {
      return {
        ...state,
        verificationData: { status: false, isLoading: false, error: action.payload, data: null },
      }
    })
    .addCase(getVerificationStatus.pending, (state) => {
      return {
        ...state,
        verificationData: { status: false, isLoading: true, error: false, data: null },
      }
    })
    .addCase(getVerification.fulfilled, (state, action) => {
      return {
        ...state,
        verificationData: { status: action.payload.status, isLoading: false, error: null },
      }
    })
    .addCase(getVerification.rejected, (state, action) => {
      return {
        ...state,
        verificationData: { status: false, isLoading: false, error: action.payload },
      }
    })
    .addCase(getVerification.pending, (state, action) => {
      return {
        ...state,
        verificationData: { status: false, isLoading: true, error: null },
      }
    })
    .addCase(getTutors.fulfilled, (state, action) => {
      return {
        ...state,
        tutors: {
          isLoading: false,
          tutors: action.payload,
        },
      }
    })
    .addCase(getTutors.rejected, (state, action) => {
      return {
        ...state,
        tutors: { isLoading: false, error: action.payload },
      }
    })
    .addCase(getTutors.pending, (state, action) => {
      return {
        ...state,
        tutors: { isLoading: true },
      }
    })
}) // Close the builder callback and createReduce

export default userReducer
const userState = (state) => state.user

export const selectIsLoading = createSelector(userState, ({ isLoading }) => isLoading)

export const selectRegisterStep = createSelector(userState, ({ registerStep, userData }) => {
  const backendStepString = registerStep ?? userData?.current_registration_step //revert back to using current step
  console.log(backendStepString)
  if (typeof backendStepString === 'string' && backendStepString) {
    return backendStepString
  }
  return registerStep
})

export const selectSwitchStep = createSelector(userState, ({ switchStep }) => switchStep)

export const selectTokenSyncComplete = createSelector(
  userState,
  ({ tokenSyncComplete }) => tokenSyncComplete
)

export const selectLoadingUpdateNamePicture = createSelector(
  userState,
  ({ loading: { updateNamePicture } }) => updateNamePicture
)

export const selectErrorUpdateNamePicture = createSelector(
  userState,
  ({ errors: { updateNamePicture } }) => updateNamePicture
)

export const selectIsLoadingCreateBanner = createSelector(
  userState,
  ({ createBanners: { isLoading: isLoadingBanner } }) => isLoadingBanner
)

export const selectStepCreateBanner = createSelector(
  userState,
  ({ createBanners: { step } }) => step
)

export const selectBannersCreateBanner = createSelector(
  userState,
  ({ createBanners: { banners } }) => banners
)

export const selectBannersPagination = createSelector(
  userState,
  ({ createBanners: { banners, bannersPage, bannersTotal, bannersPageSize } }) => ({
    banners,
    bannersPage,
    bannersTotal,
    bannersPageSize,
  })
)

export const selectPassedTestsCreateBanner = createSelector(userState, ({ passedTests }) => {
  return passedTests
})

export const selectErrorSignIn = createSelector(userState, ({ userSignIn: { error } }) => error)

export const selectAvailableEvents = createSelector(
  userState,
  ({ createBanners: { availableEvents } }) =>
    availableEvents.data.reduce((acc, item) => {
      acc[`${item.day}:${item.slot}`] = item
      return acc
    }, {})
)

export const selectAvailableEventsIsFetching = createSelector(
  userState,
  ({ createBanners: { availableEvents } }) => availableEvents.isLoading
)

export const selectTestID = createSelector(userState, ({ testID }) => testID)
export const selectAvailableEventsIsSubmitting = createSelector(
  userState,
  ({ createBanners: { availableEvents } }) => availableEvents.isSubmitting
)
export const selectAvailableEventsNewCreatedAsEntityAdapter = createSelector(
  userState,
  ({ createBanners: { availableEvents } }) =>
    availableEvents.newCreated.reduce((acc, item) => {
      acc[`${item.day}:${item.slot}`] = item
      return acc
    }, {})
)
export const selectAvailableEventsNewCreated = createSelector(
  userState,
  ({ createBanners: { availableEvents } }) => availableEvents.newCreated
)

export const selectLastCreatedBanner = createSelector(
  userState,
  ({ createBanners: { banners } }) => banners[banners.length - 1]
)

export const selectCurrentTest = createSelector(userState, ({ currentTest }) => {
  if (currentTest) {
    return currentTest
  }
})

export const selectClassBannerCategoriesCreateBanner = createSelector(
  userState,
  ({ createBanners: { classBannerCategories } }) => classBannerCategories
)

export const selectErrorCreateBanner = createSelector(
  userState,
  ({ createBanners: { error } }) => error
)

export const selectErrorMessageCreateBanner = createSelector(
  userState,
  ({ createBanners: { createBannerError } }) => createBannerError
)

export const selectSelectedCategories = createSelector(userState, ({ selectedCategories }) => {
  if (selectedCategories.length > 0) {
    return selectedCategories
  }
  return store.get('initiated_tests')
})

export const selectErrorsCreateAccount = createSelector(
  userState,
  ({ errors: { createAccount } }) => createAccount
)

export const selectErrorsUpdateAbout = createSelector(
  userState,
  ({ errors: { updateAbout } }) => updateAbout
)

export const selectUserIsSigningIn = createSelector(
  userState,
  ({ userSignIn }) => userSignIn.isLoading
)

export const selectUserData = createSelector(userState, ({ userData }) => userData)
export const selectUserLoading = createSelector(
  userState,
  ({ userProfile }) => userProfile.isLoading
)

export const selectPaymentOptions = createSelector(
  userState,
  ({ paymentOptions }) => paymentOptions
)

export const selectDeleteClassBannerLoading = createSelector(
  userState,
  ({ loading: { deleteClassBanner } }) => deleteClassBanner
)

export const selectGetClassBannerLoading = createSelector(
  userState,
  ({ loading: { deleteClassBanner } }) => deleteClassBanner
)

export const selectClassBanner = createSelector(
  userState,
  ({ createBanners: { banner } }) => banner
)

export const selectMainCategories = createSelector(
  userState,
  ({ mainCategories }) => mainCategories
)

export const selectLoadingActivateTwoFactorAuth = createSelector(
  userState,
  ({ loading: { activateTwoFactorAuth } }) => activateTwoFactorAuth
)

export const selectLoadingVerifyTwoFactorAuth = createSelector(
  userState,
  ({ loading: { verifyTwoFactorAuth } }) => verifyTwoFactorAuth
)

export const selectErrorVerifyTwoFactorAuth = createSelector(
  userState,
  ({ errors: { verifyTwoFactorAuth } }) => verifyTwoFactorAuth
)

export const selectLoadingChangePassword = createSelector(
  userState,
  ({ loading: { changePassword } }) => changePassword
)

export const selectLoadingSendResetPasswordEmail = createSelector(
  userState,
  ({ loading: { sendResetPasswordEmail } }) => sendResetPasswordEmail
)

export const selectErrorSendResetPasswordEmail = createSelector(
  userState,
  ({ errors: { sendResetPasswordEmail } }) => sendResetPasswordEmail
)

export const selectStudentGoal = createSelector(userState, ({ goal }) => goal)

export const selectLoadingCreateStudentGoal = createSelector(
  userState,
  ({ loading: { createStudentGoal } }) => createStudentGoal
)

export const selectLoadingEditStudentGoal = createSelector(
  userState,
  ({ loading: { editStudentGoal } }) => editStudentGoal
)

export const selectLoadingResetPassword = createSelector(
  userState,
  ({ loading: { resetPassword } }) => resetPassword
)

export const selectErrorResetPassword = createSelector(
  userState,
  ({ errors: { resetPassword } }) => resetPassword
)

export const selectLoadingDeactivateTwoFactorAuth = createSelector(
  userState,
  ({ loading: { deactivateTwoFactorAuth } }) => deactivateTwoFactorAuth
)

export const selectLoadingDeleteAccount = createSelector(
  userState,
  ({ loading: { deleteAccount } }) => deleteAccount
)

export const selectLoadingGetBanners = createSelector(
  userState,
  ({ loading: { getBanners } }) => getBanners
)

export const selectLoadingSubmitTest = createSelector(
  userState,
  ({ loading: { submitTest } }) => submitTest
)

export const selectLoadingCancelTest = createSelector(
  userState,
  ({ loading: { cancelTest } }) => cancelTest
)

export const selectLoadingEditClassBanner = createSelector(
  userState,
  ({ loading: { editClassBanner } }) => editClassBanner
)

export const selectErrorEditClassBanner = createSelector(
  userState,
  ({ errors: { editClassBanner } }) => editClassBanner
)

export const selectLoadingGetRandomTest = createSelector(
  userState,
  ({ loading: { getRandomTest } }) => getRandomTest
)

export const selectErrorGetRandomTest = createSelector(
  userState,
  ({ errors: { getRandomTest } }) => getRandomTest
)

export const selectLoadingGetLoginHistory = createSelector(
  userState,
  ({ loading: { getLoginHistory } }) => getLoginHistory
)

export const getInitiatedTests = createSelector(userState, ({ initiatedTests }) => {
  if (initiatedTests) {
    return initiatedTests
  }
  // return store.get('initiated_tests') || []
})

export const selectFullScreenMode = createSelector(userState, ({ fullscreen }) => {
  return fullscreen
})

export const selectLoginHistoryPagination = createSelector(
  userState,
  ({
    loginHistory: { loginHistory, loginHistoryPage, loginHistoryPageSize, loginHistoryTotal },
  }) => ({
    loginHistory,
    loginHistoryPage,
    loginHistoryPageSize,
    loginHistoryTotal,
  })
)

export const selectLoadingGetLinkedDevices = createSelector(
  userState,
  ({ loading: { getLinkedDevices } }) => getLinkedDevices
)

export const selectLinkedDevicesPagination = createSelector(
  userState,
  ({
    linkedDevices: { linkedDevices, linkedDevicesPage, linkedDevicesPageSize, linkedDevicesTotal },
  }) => ({
    linkedDevices,
    linkedDevicesPage,
    linkedDevicesPageSize,
    linkedDevicesTotal,
  })
)

export const selectInstructionError = createSelector(
  userState,
  ({ tutorInstructions: { error } }) => error
)

export const selectLoadingLogoutFromAllDevices = createSelector(
  userState,
  ({ loading: { logoutFromAllDevices } }) => logoutFromAllDevices
)

export const selectAccessToken = createSelector(userState, ({ accessToken }) => accessToken)

export const selectRole = createSelector(userState, ({ activeRole }) => activeRole)

export const selectUserProfile = createSelector(
  userState,
  ({ userData, activeRole }) =>
    userData?.[`user_${activeRole}_profile`] || userData?.profile || null
)

export const selectVerificationData = createSelector(
  userState,
  ({ verificationData }) => verificationData
)

export const selectEmail = createSelector(userState, ({ userData }) => {
  if (userData?.email) return userData?.email

  try {
    const stored = localStorage.getItem('user_data')
    return stored ? (JSON.parse(stored)?.email ?? null) : null
  } catch {
    return null
  }
})

// export const selectIsEmailConfirmed = createSelector(
//   userState,
//   ({ userData, isEmailConfirmed }) => {
//     if (userData?.email_confirmed !== undefined) {
//       return userData.email_confirmed
//     }
//     return isEmailConfirmed
//   }
// )
